<template lang="pug">
#waiter-change-request(v-cloak)
  .box
    .box-header.with-border
      .d-flex.with-border
        .title
          b-spinner.spinner.mr-2(v-if="waiter == null")
          span(v-else-if="waiter.name") {{ waiter.name }}
          span(v-else).warning-message 未入力
          span 様の変更申請
    .box-body
      .message-container(v-if="!existRoomType")
        .warning-message
          div 本施設、グループ施設に申込可能な部屋が設定されていないため、登録できません。
          div 設定をご確認ください。

      .template-container(v-if="changeRequest && existRoomType")
        .template(v-for="(property, index) in changeRequest.afterValue.properties")
          WaiterTemplateDiffInputItem(v-model="changeRequest.afterValue.properties[index]"
            :beforeValue="changeRequest.beforeValue.properties[index]" :waiterViewableFacilitiesMap="waiterViewableFacilitiesMap" :isChaild="false")

    .box-footer.with-border.register-footer
      ValidationAlert(v-model="showValidationAlert")
      .buttons
        button.btn.acceptance-btn(@click="showAcceptanceMessageInputModal" type="button") 受理（入所申込に反映）
        button.btn.rejection-btn(@click="showRejectionMessageInputModal" type="button") 不受理（申込者に通知）
        button.btn.cancel-btn(@click="gotoWaiterDetail" type="button") キャンセル

      .register-row(v-show="errorMessage")
        .register-group
          .register-value
            .error-message {{ errorMessage }}

  WaiterChangeApplicationMessageModal(v-model="showAcceptanceMessageModal" modalTitle="変更申請の受理メッセージを入力してください"
    :title.sync="messageTitle" :text.sync="messageText" @onSend="showAcceptanceConfirm")
  WaiterChangeApplicationMessageConfirmModal(v-model="showAcceptanceConfirmModal" message="変更申請を受理し、メッセージを送信します。よろしいですか？"
    :title="messageTitle" :text="messageText" @onSend="acceptance")
  OkModal(v-model="showAcceptanceSuccessModal" title="変更申請を受理しました" @ok="gotoWaiterDetail")

  WaiterChangeApplicationMessageModal(v-model="showRejectionMessageModal" modalTitle="変更申請の不受理メッセージを入力してください"
    :title.sync="messageTitle" :text.sync="messageText" @onSend="showRejectionConfirm")
  WaiterChangeApplicationMessageConfirmModal(v-model="showRejectionConfirmModal" message="変更申請を不受理にし、メッセージを送信します。よろしいですか？"
    :title="messageTitle" :text="messageText" @onSend="rejection")
  OkModal(v-model="showRejectionSuccessModal" title="変更申請を不受理にしました" @ok="gotoWaiterDetail")

  PageLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";
import { needRefreshesStore } from "@/store/store";
import { WaiterListState, SendWaiterMessagesState } from "@/store/NeedRefreshes";
import { PcUrl } from "@/commons/constants/PcUrl";
import SystemError from "@/commons/models/SystemError";

import Setting from "../../../../public/setting.json";
import CustomValidate from "@/commons/utils/CustomValidate";
import LoginUser from "@/store/LoginUser";

import FacilitiesService, { FacilityGetModel } from "@/commons/services/Facilities";
import WaitersService, { WaitersGetModel } from "@/commons/services/Waiters";
import WaiterChangeApplicationsService, {
  WaiterChangeApplicationsGetModel,
  WaiterChangeApplicationsPatchModel,
  WaiterChangeApplicationsPostModel
} from "@/commons/services/WaiterChangeApplications";
import S3UrlService from "@/commons/services/S3Url";
import {
  TemplateItem,
  TemplateItemBirthday,
  TemplateItemFile,
  TemplateItemGroup,
  TemplateItemInput,
  TemplateItemRadioGroup,
  TemplateItemRoomType,
  TemplateItemSection,
  TemplateItemSwitchCheckbox,
  TemplateType
} from "@/commons/services/AdmissionApplicationTemplates";

import PageLoading from "@/pc/basics/PageLoading.vue";
import OkModal from "@/pc/basics/OkModal.vue";
import OkCancelModal from "@/pc/basics/OkCancelModal.vue";
import ValidationAlert from "@/pc/basics/ValidationAlertByValidator.vue";
import WaiterTemplateDiffInputItem from "@/pc/containers/waiters/WaiterTemplateDiffInputItem.vue";
import WaiterChangeApplicationMessageModal from "@/pc/containers/waiters/WaiterChangeApplicationMessageModal.vue";
import WaiterChangeApplicationMessageConfirmModal from "@/pc/containers/waiters/WaiterChangeApplicationMessageConfirmModal.vue";

/**
 * ### 待機者変更申請画面コンポーネント
 */
@Component({
  components: {
    PageLoading,
    OkCancelModal,
    OkModal,
    ValidationAlert,
    WaiterTemplateDiffInputItem,
    WaiterChangeApplicationMessageModal,
    WaiterChangeApplicationMessageConfirmModal
  },
  $_veeValidate: { validator: "new" }
})
export default class WaiterChangeRequest extends Vue {
  /** 施設サービス */
  @Inject("FacilitiesService") facilitiesService!: FacilitiesService;
  /** 待機者サービス */
  @Inject("WaitersService") waitersService!: WaitersService;
  /** 待機者サービス */
  @Inject("WaiterChangeApplicationsService") waiterChangeApplicationsService!: WaiterChangeApplicationsService;
  /** S3Urlサービス */
  @Inject("S3UrlService") s3UrlService!: S3UrlService;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";

  /** 受理メッセージ入力モーダル表示可否 */
  showAcceptanceMessageModal: boolean = false;
  /** 受理確認モーダル表示可否 */
  showAcceptanceConfirmModal: boolean = false;
  /** 受理完了モーダル表示可否 */
  showAcceptanceSuccessModal: boolean = false;
  /** 不受理メッセージ入力モーダル表示可否 */
  showRejectionMessageModal: boolean = false;
  /** 不受理確認モーダル表示可否 */
  showRejectionConfirmModal: boolean = false;
  /** 不受理完了モーダル表示可否 */
  showRejectionSuccessModal: boolean = false;

  /** 受理・不受理メッセージ入力モーダルタイトル */
  messageTitle: string = "";
  /** 受理・不受理メッセージ入力モーダル本文 */
  messageText: string = "";

  /** バリデーションアラート表示可否 */
  showValidationAlert: boolean = false;

  /** 部屋種類が存在するか否か */
  existRoomType: boolean = true;

  /** 閲覧可能施設のMAP、key：施設ID、value：施設情報 */
  waiterViewableFacilitiesMap: Map<number, FacilityGetModel> = new Map<number, FacilityGetModel>();
  /** 更新対象の待機者 */
  waiter: WaitersGetModel | null = null;
  /** 変更申請 */
  changeRequest: WaiterChangeApplicationsGetModel | null = null;

  /** エラーメッセージ */
  errorMessage: string = "";
  /** 確認モーダル用エラーが存在するか */
  validInputError: boolean = true;

  /**
   * ### vue lifecycle created
   */
  public created(): void {
    CustomValidate.addCustomValidate(this.$validator);
    return;
  }

  /**
   * ### vue lifecycle created
   */
  public async mounted(): Promise<void> {
    await this.createView();

    return;
  }

  /**
   * ### 画面情報作成
   */
  public async createView(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    const waiterViewableFacilities: Array<FacilityGetModel> = await this.getWaiterViewableFacilities(userStore);
    this.existRoomType = this.getExistRoomType(userStore.facility, waiterViewableFacilities);

    const waiterViewableFacilitiesMap: Map<number, FacilityGetModel> = this.createWaiterViewableFacilitiesMap(waiterViewableFacilities);
    this.waiterViewableFacilitiesMap = waiterViewableFacilitiesMap;

    const facilityId: number = Number(this.$route.params.facilityid);
    const residentId: number = Number(this.$route.params.residentid);
    const idinsystem: number = Number(this.$route.params.idinsystem);
    const waiter: WaitersGetModel = await this.getWaiter(facilityId, residentId, idinsystem);
    this.waiter = waiter;

    const idInsystem: number = Number(this.$route.params.idinsystem);
    const changeRequest: WaiterChangeApplicationsGetModel = await this.getWaiterChangeApplications(idInsystem);
    this.changeRequest = changeRequest;

    await this.setS3Files(this.changeRequest);

    this.isBusy = false;

    return;
  }

  /**
   * ### 閲覧可能施設情報を取得する
   */
  public async getWaiterViewableFacilities(userStore: LoginUser): Promise<Array<FacilityGetModel>> {
    const waiterViewableFacilities: Array<FacilityGetModel> = [];
    for (const facilityId of userStore.facility.waiterViewableFacilities) {
      const response: FacilityGetModel = await this.facilitiesService.get(userStore, { targetFacilityId: facilityId });
      waiterViewableFacilities.push(response);
    }
    return waiterViewableFacilities;
  }

  /**
   * ### 閲覧可能施設情報のMAPを作成する
   */
  public createWaiterViewableFacilitiesMap(waiterViewableFacilities: Array<FacilityGetModel>): Map<number, FacilityGetModel> {
    const waiterViewableFacilitiesMap: Map<number, FacilityGetModel> = new Map<number, FacilityGetModel>();
    waiterViewableFacilitiesMap.set(userStore.facility.id, userStore.facility);
    for (const facility of waiterViewableFacilities) {
      waiterViewableFacilitiesMap.set(facility.id, facility);
    }
    return waiterViewableFacilitiesMap;
  }

  /**
   * ### 部屋種類が存在するかを返す
   */
  public getExistRoomType(mineFacility: FacilityGetModel, waiterViewableFacilities: Array<FacilityGetModel>): boolean {
    const mineFacilityExistRoomType: boolean = this.isRecruitmentRoom(mineFacility) || this.isCustomRequiredRecruitmentRoom(mineFacility);

    let waiterViewableFacilitiesEmptyRoomType: boolean = false;
    for (const viewableFacility of waiterViewableFacilities) {
      const existRoomType: boolean = this.isRecruitmentRoom(viewableFacility) || this.isCustomRequiredRecruitmentRoom(viewableFacility);
      waiterViewableFacilitiesEmptyRoomType = waiterViewableFacilitiesEmptyRoomType || existRoomType;
    }

    return mineFacilityExistRoomType || waiterViewableFacilitiesEmptyRoomType;
  }

  public isRecruitmentRoom(facility: FacilityGetModel): boolean {
    if (
      (facility.recruitmentRoom.multiBed ||
        facility.recruitmentRoom.conventinalSingle ||
        facility.recruitmentRoom.unitTypeSingle ||
        facility.recruitmentRoom.double ||
        facility.recruitmentRoom.single) == true
    ) {
      return true;
    } else {
      return false;
    }
  }

  public isCustomRequiredRecruitmentRoom(facility: FacilityGetModel): boolean {
    if (!facility.customRecruitmentRoom) {
      return false;
    }
    for (const room of facility.customRecruitmentRoom) {
      if (room.use == true) {
        return true;
      }
    }
    return false;
  }

  /**
   * ### 待機者を取得する
   */
  public async getWaiter(facilityId: number, residentId: number, idinsystem: number): Promise<WaitersGetModel> {
    const waitersByIdInSystem: Array<WaitersGetModel> = await this.waitersService.getsByIdInSystem(userStore, idinsystem);
    for (const waiter of waitersByIdInSystem) {
      if (waiter.facilityId == facilityId) {
        return waiter;
      }
    }
    // 見つからない場合は個別取得する
    const waiter: WaitersGetModel = await this.waitersService.get(userStore, facilityId, residentId);
    return waiter;
  }

  /**
   * ### 変更申請を取得する
   */
  public async getWaiterChangeApplications(idInsystem: number): Promise<WaiterChangeApplicationsGetModel> {
    const record: WaiterChangeApplicationsGetModel = await this.waiterChangeApplicationsService.get(userStore, idInsystem);
    return record;
  }

  /**
   * ### ファイルをS3から取得してFileオブジェクトにしてテンプレート情報に格納する
   *
   * - 変更前ファイルの復元
   * - 変更後ファイルの復元
   */
  public async setS3Files(changeRequest: WaiterChangeApplicationsGetModel): Promise<void> {
    let beforeFileItems: Array<TemplateItemFile> = [];
    this.searchFileItems(beforeFileItems, changeRequest.beforeValue.properties);

    for (const fileItem of beforeFileItems) {
      if (fileItem.answer != null) {
        if (fileItem.tempAnswer == null) {
          fileItem.tempAnswer = [];
        }

        for (const beforeFile of fileItem.answer) {
          if (beforeFile && beforeFile.url && beforeFile.name) {
            let s3File: File = await this.getS3File(beforeFile.url, beforeFile.name);
            fileItem.tempAnswer.push({
              file: s3File,
              fileName: beforeFile.name
            });
          }
        }
      } else {
        fileItem.answer = [];
        if (fileItem.tempAnswer == null) {
          fileItem.tempAnswer = [];
        }
      }
    }

    let afterFileItems: Array<TemplateItemFile> = [];
    this.searchFileItems(afterFileItems, changeRequest.afterValue.properties);

    for (const fileItem of afterFileItems) {
      if (fileItem.answer != null) {
        if (fileItem.tempAnswer == null) {
          fileItem.tempAnswer = [];
        }

        for (const afterFile of fileItem.answer) {
          if (afterFile && afterFile.url && afterFile.name) {
            let s3File: File = await this.getS3File(afterFile.url, afterFile.name);
            fileItem.tempAnswer.push({
              file: s3File,
              fileName: afterFile.name
            });
          }
        }
      } else {
        fileItem.answer = [];
        if (fileItem.tempAnswer == null) {
          fileItem.tempAnswer = [];
        }
      }
    }

    return;
  }

  /**
   * ### Fileオブジェクトを探して返す
   */
  public searchFileItems(fileItems: Array<TemplateItemFile>, properties: Array<TemplateItem>): void {
    for (const property of properties) {
      if (this.hasProperties(property) == true) {
        if (property.type == TemplateType.SECTION) {
          const item: TemplateItemSection = property as TemplateItemSection;

          if (item.properties && Array.isArray(item.properties)) {
            // LIFF側でpropertiesをNULLにしてしまう場合があるため、チェックする
            this.searchFileItems(fileItems, item.properties);
          }
        } else if (property.type == TemplateType.GROUP) {
          const item: TemplateItemGroup = property as TemplateItemGroup;

          if (item.properties && Array.isArray(item.properties)) {
            // LIFF側でpropertiesをNULLにしてしまう場合があるため、チェックする
            for (const childProperties of item.properties) {
              if (childProperties && Array.isArray(childProperties)) {
                this.searchFileItems(fileItems, childProperties);
              }
            }
          }
        } else if (property.type == TemplateType.RADIO_GROUP) {
          const item: TemplateItemRadioGroup = property as TemplateItemRadioGroup;

          if (item.properties && Array.isArray(item.properties)) {
            // LIFF側でpropertiesをNULLにしてしまう場合があるため、チェックする
            for (const childProperties of item.properties) {
              if (childProperties && Array.isArray(childProperties.properties)) {
                this.searchFileItems(fileItems, childProperties.properties);
              }
            }
          }
        } else if (property.type == TemplateType.SWITCH_CHECKBOX) {
          const item: TemplateItemSwitchCheckbox = property as TemplateItemSwitchCheckbox;

          // LIFF側でpropertiesをNULLにしてしまう場合があるため、チェックする
          if (item.properties && Array.isArray(item.properties)) {
            this.searchFileItems(fileItems, item.properties);
          }
        }
      }

      const inputItem: TemplateItemInput = property as TemplateItemInput;
      if (inputItem.type == TemplateType.FILE) {
        const fileItem: TemplateItemFile = inputItem as TemplateItemFile;

        if (fileItem.tempAnswer || fileItem.answer) {
          fileItems.push(fileItem);
        }
      }
    }
  }

  /**
   * ### propertiesを持つテンプレート要素かどうかを取得する
   */
  public hasProperties(item: TemplateItem): boolean {
    switch (item.type) {
      case TemplateType.SECTION:
      case TemplateType.GROUP:
      case TemplateType.RADIO_GROUP:
      case TemplateType.SWITCH_CHECKBOX: {
        return true;
      }
      default: {
        return false;
      }
    }
  }

  /**
   * ### S3に格納されているFileを取得する
   *
   * @param url 元URL
   */
  public async getS3File(url: string, fileName: string): Promise<File> {
    const response: File = await this.s3UrlService.getFile(userStore, Setting.backets.imageExpireHour, url, fileName);
    return response;
  }

  /**
   * ### 変更申請受理メッセージ入力モーダルを表示する
   */
  public async showAcceptanceMessageInputModal(): Promise<void> {
    const validationResult: boolean = await this.$validator.validateAll();

    // 参照渡しだとエラー有無が上手く設定できないのでvalidInputErrorというクラス内のグローバルプロパティにエラー有無は設定する
    this.validInputError = true;
    const changeRequest: WaiterChangeApplicationsGetModel = this.changeRequest as WaiterChangeApplicationsGetModel;
    await this.validateBirthday(changeRequest.afterValue.properties);
    await this.validateRoomType(changeRequest.afterValue.properties);

    if (validationResult && this.validInputError) {
      this.showValidationAlert = false;
      const waiter: WaitersGetModel = this.waiter as WaitersGetModel;
      this.messageTitle = "変更申請受理";
      this.messageText = `${waiter.name}様の変更申請が${userStore.facility.name}から受理されました。`;

      this.showAcceptanceMessageModal = true;
    } else {
      this.showValidationAlert = true;
    }

    return;
  }

  /**
   * ### 変更申請受理確認モーダルを表示する
   */
  public showAcceptanceConfirm(): void {
    this.showAcceptanceConfirmModal = true;
    return;
  }

  /**
   * ### 生年月日の存在するかしないかのバリデーション
   *
   * - 生年月日が1つでも入力されていればバリデーション対象として存在確認する
   * - 生年月日が1つも入力されていない場合はバリデーションせず、OKとする
   */
  public async validateBirthday(properties: Array<TemplateItem>): Promise<void> {
    for (const property of properties) {
      if (this.hasProperties(property) == true) {
        if (property.type == TemplateType.SECTION) {
          const item: TemplateItemSection = property as TemplateItemSection;

          if (item.properties && Array.isArray(item.properties)) {
            // LIFF側でpropertiesをNULLにしてしまう場合があるため、チェックする
            await this.validateBirthday(item.properties);
          }
        } else if (property.type == TemplateType.GROUP) {
          const item: TemplateItemGroup = property as TemplateItemGroup;

          if (item.properties && Array.isArray(item.properties)) {
            // LIFF側でpropertiesをNULLにしてしまう場合があるため、チェックする
            for (const childProperties of item.properties) {
              if (childProperties && Array.isArray(childProperties)) {
                await this.validateBirthday(childProperties);
              }
            }
          }
        } else if (property.type == TemplateType.RADIO_GROUP) {
          const item: TemplateItemRadioGroup = property as TemplateItemRadioGroup;

          if (item.properties && Array.isArray(item.properties)) {
            // LIFF側でpropertiesをNULLにしてしまう場合があるため、チェックする
            for (const childProperties of item.properties) {
              if (childProperties && Array.isArray(childProperties.properties)) {
                await this.validateBirthday(childProperties.properties);
              }
            }
          }
        } else if (property.type == TemplateType.SWITCH_CHECKBOX) {
          const item: TemplateItemSwitchCheckbox = property as TemplateItemSwitchCheckbox;

          // LIFF側でpropertiesをNULLにしてしまう場合があるため、チェックする
          if (item.properties && Array.isArray(item.properties)) {
            await this.validateBirthday(item.properties);
          }
        }
      }

      const inputItem: TemplateItemInput = property as TemplateItemInput;
      if (inputItem.type == TemplateType.BIRTHDAY) {
        const birthdayItem: TemplateItemBirthday = inputItem as TemplateItemBirthday;

        if (birthdayItem.answer.year || birthdayItem.answer.month || birthdayItem.answer.date) {
          const birthday: number = this.joinBirthday(birthdayItem);
          const { valid, errors } = await this.$validator.verify(birthday, "exist_date", { name: "本項目", bails: false });

          if (!valid) {
            for (const error of errors) {
              this.$validator.errors.add({ field: birthdayItem.id, msg: error });
            }
            this.validInputError = false;
          } else {
            this.$validator.errors.remove(birthdayItem.id);
          }
        }
      }
    }
  }

  public joinBirthday(birthdayItem: TemplateItemBirthday): number {
    let month: string = String(birthdayItem.answer.month);
    if (month.length == 1) {
      month = `0${month}`;
    }

    let date: string = String(birthdayItem.answer.date);
    if (date.length == 1) {
      date = `0${date}`;
    }

    const birthday: number = Number(`${birthdayItem.answer.year}${month}${date}000000000`);

    return birthday;
  }

  /**
   * ### 部屋種別のバリデーション
   */
  public async validateRoomType(properties: Array<TemplateItem>): Promise<void> {
    for (const property of properties) {
      if (this.hasProperties(property) == true) {
        if (property.type == TemplateType.SECTION) {
          const item: TemplateItemSection = property as TemplateItemSection;

          if (item.properties && Array.isArray(item.properties)) {
            // LIFF側でpropertiesをNULLにしてしまう場合があるため、チェックする
            await this.validateRoomType(item.properties);
          }
        } else if (property.type == TemplateType.GROUP) {
          const item: TemplateItemGroup = property as TemplateItemGroup;

          if (item.properties && Array.isArray(item.properties)) {
            // LIFF側でpropertiesをNULLにしてしまう場合があるため、チェックする
            for (const childProperties of item.properties) {
              if (childProperties && Array.isArray(childProperties)) {
                await this.validateRoomType(childProperties);
              }
            }
          }
        } else if (property.type == TemplateType.RADIO_GROUP) {
          const item: TemplateItemRadioGroup = property as TemplateItemRadioGroup;

          if (item.properties && Array.isArray(item.properties)) {
            // LIFF側でpropertiesをNULLにしてしまう場合があるため、チェックする
            for (const childProperties of item.properties) {
              if (childProperties && Array.isArray(childProperties.properties)) {
                await this.validateRoomType(childProperties.properties);
              }
            }
          }
        } else if (property.type == TemplateType.SWITCH_CHECKBOX) {
          const item: TemplateItemSwitchCheckbox = property as TemplateItemSwitchCheckbox;

          // LIFF側でpropertiesをNULLにしてしまう場合があるため、チェックする
          if (item.properties && Array.isArray(item.properties)) {
            await this.validateRoomType(item.properties);
          }
        }
      }

      const inputItem: TemplateItemInput = property as TemplateItemInput;
      if (inputItem.type == TemplateType.ROOM_TYPE) {
        const roomTypeItem: TemplateItemRoomType = inputItem as TemplateItemRoomType;

        let required: boolean = false;
        for (const validationRule of roomTypeItem.validationRules) {
          if (validationRule == "required") {
            required = true;
            break;
          }
        }

        if (required == true) {
          this.$validator.errors.remove(roomTypeItem.id);

          let isRoomInputTrue: boolean = false;
          let isCustomRoomInputTrue: boolean = false;
          for (const answer of roomTypeItem.answer) {
            isRoomInputTrue =
              answer.recruitmentRoom.multiBed ||
              answer.recruitmentRoom.conventinalSingle ||
              answer.recruitmentRoom.unitTypeSingle ||
              answer.recruitmentRoom.double ||
              answer.recruitmentRoom.single;

            if (answer.customRecruitmentRoom) {
              for (const customRoom of answer.customRecruitmentRoom) {
                if (customRoom.use == true) {
                  isCustomRoomInputTrue = true;
                  break;
                }
              }
            }

            if (isRoomInputTrue == true || isCustomRoomInputTrue == true) {
              break;
            }
          }

          if (isRoomInputTrue == false && isCustomRoomInputTrue == false) {
            this.$validator.errors.add({ field: roomTypeItem.id, msg: "本項目は必須入力です" });
            this.validInputError = false;
          } else {
            this.$validator.errors.remove(roomTypeItem.id);
          }
        }
      }
    }
  }

  /**
   * ### 変更申請を受理する
   */
  public async acceptance(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "更新中...";

    try {
      const idInsystem: number = Number(this.$route.params.idinsystem);
      const changeRequest: WaiterChangeApplicationsGetModel = this.changeRequest as WaiterChangeApplicationsGetModel;
      const requestBody: WaiterChangeApplicationsPatchModel = {
        value: changeRequest.afterValue,
        messageTitle: this.messageTitle,
        messageText: this.messageText,
        updated: changeRequest.updated
      };
      await this.waiterChangeApplicationsService.patch(userStore, idInsystem, requestBody);

      // 待機者一覧を再読み込みさせるようstoreを更新
      let waiterList: WaiterListState = needRefreshesStore.waiterList;
      waiterList.waiterList = true;
      needRefreshesStore.setWaiterList(waiterList);
      // メッセージ送信(待機者)の宛先が変更になるので再読み込みさせるようstoreを更新
      let sendWaiterMessageState: SendWaiterMessagesState = needRefreshesStore.sendWaiterMessage;
      sendWaiterMessageState.sendMessages = true;
      needRefreshesStore.setSendWaiterMessage(sendWaiterMessageState);

      this.showAcceptanceMessageModal = false;
      this.showAcceptanceConfirmModal = false;
      this.showAcceptanceSuccessModal = true;
    } catch (error) {
      const systemError: SystemError = error as SystemError;
      // ステータスコードが409の場合、他端末にて更新が行われているので、画面情報を再取得してから再入力を促す
      if (systemError.statusCode == 409) {
        await this.createView();
        this.errorMessage = "他の端末で更新されたか、表示している情報が古いために更新できませんでした。情報を再読み込みして、もう一度お試しください。";
      }
      // その他のステータスコードの場合は上位に例外を投げなおす
      else {
        throw error;
      }
    } finally {
      this.isBusy = false;
    }

    return;
  }

  /**
   * ### 変更申請不受理メッセージ入力モーダルを表示する
   */
  public showRejectionMessageInputModal(): void {
    const waiter: WaitersGetModel = this.waiter as WaitersGetModel;
    this.messageTitle = "変更申請不受理";
    this.messageText = `${waiter.name}様の変更申請が${userStore.facility.name}から不受理となりました。`;

    this.showRejectionMessageModal = true;

    return;
  }

  /**
   * ### 変更申請不受理確認モーダルを表示する
   */
  public showRejectionConfirm(): void {
    this.showRejectionConfirmModal = true;
    return;
  }

  /**
   * ### 変更申請を不受理にする
   */
  public async rejection(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "更新中...";

    try {
      const idInsystem: number = Number(this.$route.params.idinsystem);
      const requestBody: WaiterChangeApplicationsPostModel = {
        messageTitle: this.messageTitle,
        messageText: this.messageText
      };
      await this.waiterChangeApplicationsService.delete(userStore, idInsystem, requestBody);

      this.showRejectionMessageModal = false;
      this.showRejectionConfirmModal = false;
      this.showRejectionSuccessModal = true;
    } catch (error) {
      const systemError: SystemError = error as SystemError;
      // ステータスコードが409の場合、他端末にて更新が行われているので、画面情報を再取得してから再入力を促す
      if (systemError.statusCode == 409) {
        await this.createView();
        this.errorMessage = "他の端末で更新されたか、表示している情報が古いために更新できませんでした。情報を再読み込みして、もう一度お試しください。";
      }
      // その他のステータスコードの場合は上位に例外を投げなおす
      else {
        throw error;
      }
    } finally {
      this.isBusy = false;
    }

    return;
  }

  /**
   * ### 待機者詳細に遷移する
   */
  public gotoWaiterDetail(): void {
    this.$router.push({
      name: PcUrl.WaiterDetail,
      params: { facilityid: this.$route.params.facilityid, idinsystem: this.$route.params.idinsystem, residentid: this.$route.params.residentid }
    });
    return;
  }
}
</script>

<style lang="scss" scoped>
@import "../../../styles/pc/_colors.scss";

#waiter-change-request {
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;

  .box-header {
    .title {
      font-size: 22px;
      font-weight: bold;
    }
    .spinner {
      width: 22px;
      height: 22px;
    }
  }
}
</style>
